import React, { useState, useMemo } from 'react';
import { Target, Edit3 } from 'lucide-react';
import Modal from '../../design-system/Modal';
import Button from '../../design-system/Button';
import { getAlternativeMissions, MISSION_TYPES } from '../../lib/dailyMission';

/**
 * APEX v7.7.0 — DailyMissionSwapModal
 *
 * Props :
 *   - currentMission : mission active (pour info)
 *   - onClose : ferme la modale
 *   - onSelect(newMission) : appelle swapDailyMission(newMissionData)
 *   - games, championPool : pour générer les alternatives
 */
export default function DailyMissionSwapModal({
  currentMission,
  onClose,
  onSelect,
  games = [],
  championPool = []
}) {
  const alternatives = useMemo(
    () => getAlternativeMissions({ games, championPool }),
    [games, championPool]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [customLabel, setCustomLabel] = useState('');
  const [customDescription, setCustomDescription] = useState('');

  const handleValidate = () => {
    if (selectedIndex === alternatives.length) {
      if (!customLabel.trim()) return;
      const today = new Date().toISOString().slice(0, 10);
      onSelect({
        type: MISSION_TYPES.MAINTAIN,
        label: customLabel.trim(),
        description: customDescription.trim() || 'Mission personnalisée',
        target: { games: 1 },
        reason: 'Mission définie manuellement',
        auto_generated: false,
        created_for_date: today
      });
    } else {
      onSelect(alternatives[selectedIndex]);
    }
  };

  const isValid = selectedIndex < alternatives.length || customLabel.trim().length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title="Choisir ma mission du jour"
      subtitle={<span className="flex items-center gap-1"><Target size={10} /> Mission du jour</span>}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={handleValidate} disabled={!isValid}>Valider la mission</Button>
        </>
      }
    >

      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {currentMission?.data && (
          <div className="px-3 py-2 bg-zinc-900/40 border border-zinc-800/60 mb-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Mission actuelle</div>
            <div className="text-xs text-zinc-300">{currentMission.data.label}</div>
          </div>
        )}
          {alternatives.map((alt, idx) => (
            <label
              key={idx}
              className={`flex items-start gap-3 p-3 border cursor-pointer transition ${
                selectedIndex === idx
                  ? 'border-cyan-400/80 bg-cyan-400/5'
                  : 'border-zinc-800 hover:border-zinc-600'
              }`}
            >
              <input
                type="radio"
                name="mission"
                checked={selectedIndex === idx}
                onChange={() => setSelectedIndex(idx)}
                className="mt-1 accent-cyan-400"
              />
              <div className="flex-1">
                <div className="text-sm font-bold text-white">{alt.label}</div>
                <div className="text-[12px] text-zinc-400 mt-0.5">{alt.description}</div>
              </div>
            </label>
          ))}

          <label
            className={`flex items-start gap-3 p-3 border cursor-pointer transition ${
              selectedIndex === alternatives.length
                ? 'border-fuchsia-400/80 bg-fuchsia-400/5'
                : 'border-zinc-800 hover:border-zinc-600'
            }`}
          >
            <input
              type="radio"
              name="mission"
              checked={selectedIndex === alternatives.length}
              onChange={() => setSelectedIndex(alternatives.length)}
              className="mt-1 accent-fuchsia-400"
            />
            <div className="flex-1 space-y-2">
              <div className="text-sm font-bold text-white flex items-center gap-2">
                <Edit3 size={12} /> Saisie libre
              </div>
              {selectedIndex === alternatives.length && (
                <>
                  <input
                    type="text"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder="Ex : 3 games sans tilt"
                    maxLength={60}
                    className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-sm text-white placeholder:text-zinc-600 focus:border-fuchsia-400 outline-none"
                    autoFocus
                  />
                  <input
                    type="text"
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    placeholder="Description (optionnel)"
                    maxLength={100}
                    className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-xs text-zinc-300 placeholder:text-zinc-600 focus:border-fuchsia-400 outline-none"
                  />
                </>
              )}
            </div>
          </label>
        </div>

    </Modal>
  );
}
